import {
    ComponentObject,
    ComponentSelectors,
    ComponentElements,
    ComponentInjector,
    UIComponent, ComponentService,
} from '@atypon/ui-utils';
import { VPortListener } from "ui-core/plugins/enquireJs/js/helper"


class Texts extends ComponentObject {
    doi = '';
    apiUrl = '';
    isCrossrefEnabled = '';
    getFTRContent = '';
    getFTRButton = '';
    getFTRLink = '';
    prevItemDoi = '';
    requestSource = '';
}

class ClassList extends ComponentObject {
    getFTRPlaceholder = 'getFTR__placeholder';
}

class Selectors extends ComponentSelectors {
    article = 'article';
    articleReferences = 'article .article-section__references';
    articleCitedBy = 'article .article-section__citedBy'
    articleCitedByAccordionControl = '.article-section__citedBy .accordion__control'
    tabContent = '.tab--slide .tab__content';
    tabReferences = '#pane-pcw-references';
    tabRelated = '#pane-pcw-related';
    mobileTabContent = '.w-slide__content';
    getFTRPlaceholder = '.getFTR__placeholder';
    getFTRBtn = '.getFTR__btn';
    metaDoi = 'meta[name="dc.identifier"]';
    wrapperItem = ''; // Reference or citedBy item
}

class Elements extends ComponentElements<Selectors> {
    article: HTMLElement;
    articleReferences: HTMLElement;
    articleCitedBy: HTMLElement;
    articleCitedByAccordionControl: HTMLElement;
    tabContent: HTMLElement;
    tabReferences: HTMLElement;
    tabRelated: HTMLElement;
    getFTRPlaceholderElements: Array<HTMLElement> = [];
    getFTRWrapper: HTMLElement;
    getFTRPlaceholderMatched: Array<HTMLElement> = [];
    getFTRPlaceholderBatch: Array<HTMLElement> = [];
    metaDoi: HTMLElement;
    config: HTMLElement; // article or cited-by accordion control element
    source: HTMLElement // References tab, related tab, article references or article citedBy
}

interface GetFTR extends ComponentService<Selectors, Elements, ClassList, Texts> {}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class GetFTR implements UIComponent {
    isButtonVisible: boolean = false;
    linksCount: number;
    isSafeToFetch : boolean = false;
    isFirstRequest: boolean = true;
    getFTRPlaceholderDOIsBatch: Set<string>;

    @VPortListener('md')
    isTablet : boolean;

    isTabletOn() {
        this.isTablet = true;
    }

    isTabletOff() {
        this.isTablet = false;
    }

    initialize(): void {
        this.setElements();
        this.addEventListeners();
    }

    private setElements(): void {
        this.elements.article = this.domUtils.getElement<HTMLElement>(this.selectors.article);
        this.elements.metaDoi = this.domUtils.getElement<HTMLElement>(this.selectors.metaDoi, document.head);
        this.elements.getFTRPlaceholderElements = this.domUtils.getElements(this.selectors.getFTRPlaceholder);
    }

    addEventListeners(): void {
        window.addEventListener('scroll', this.handleWindowScroll);
        this.elements.tabContent?.addEventListener('scroll', () =>{
            this.handleTabScroll(this.elements.tabReferences)
        });
    }

    private getTabletElements(): void {
        if (!this.isTablet || !this.elements.tabReferences) return;

        this.elements.tabReferences = this.domUtils.getElement<HTMLElement>(this.selectors.tabReferences);
        const tabSelector = this.isFirstRequest ? this.selectors.tabContent : this.selectors.mobileTabContent;
        this.elements.tabContent = this.domUtils.getElement<HTMLElement>(tabSelector);
        this.elements.source = this.elements.tabContent?.querySelector(this.selectors.tabReferences);
        this.isFirstRequest = false;
    }


    private getGetFTRConfig(): void {
        this.texts.isCrossrefEnabled = this.elements.config.getAttribute('data-crossref-enabled');
        this.texts.requestSource = this.elements.config.getAttribute('data-getFTR-request-source');
        this.texts.doi = this.elements.metaDoi?.getAttribute('content');
        this.linksCount = parseInt(this.elements.config.getAttribute('data-getFTR-links-count'));
    }

    private setGetFTRSource(sourceElement: HTMLElement, configElement: HTMLElement, apiUrl: string): void {
        this.elements.config = configElement;
        this.initialize();
        this.getGetFTRConfig();
        this.elements.source = sourceElement;
        this.texts.apiUrl = this.elements.config.getAttribute(apiUrl);
        this.getGetFTRElements();
    }

    private getGetFTRElements(): void {
        this.getFTRPlaceholderDOIsBatch = new Set<string>();
        this.getTabletElements();
        this.elements.getFTRPlaceholderBatch = this.domUtils.getElements(this.selectors.getFTRPlaceholder, this.elements.source).slice(0, this.linksCount);
        const firstPlaceholderElement = this.elements.getFTRPlaceholderBatch && this.elements.getFTRPlaceholderBatch[0];
        const dataDoiElement = firstPlaceholderElement?.previousElementSibling;
        const firstItemDoi = dataDoiElement?.textContent;
        const getFTRPlaceholderDOIsBatch =this.elements.getFTRPlaceholderBatch?.map(element => element.previousElementSibling?.textContent).filter(Boolean); // to remove any null or undefined values from the array

        getFTRPlaceholderDOIsBatch?.forEach(doi => {
            this.getFTRPlaceholderDOIsBatch.add(doi);
        });

        // Convert Sets to Arrays
        const uniqueDOIs = Array.from(this.getFTRPlaceholderDOIsBatch);

        if (uniqueDOIs.length > 0  && (firstItemDoi === '' || firstItemDoi !== this.texts.prevItemDoi)) {
            this.getTabletElements();
            this.texts.prevItemDoi = firstItemDoi;
            this.fetchData(uniqueDOIs)
            this.elements.tabContent?.addEventListener('scroll', () =>{
                this.handleTabScroll(this.elements.source)
            });
        }
    }

    referencesTab(): void {
        this.elements.tabReferences = this.domUtils.getElement<HTMLElement>(this.selectors.tabReferences);
        this.elements.tabContent = this.domUtils.getElement<HTMLElement>(this.isTablet ? this.selectors.mobileTabContent : this.selectors.tabContent);
        this.elements.article = this.domUtils.getElement<HTMLElement>(this.selectors.article);
        this.setGetFTRSource(this.elements.tabReferences, this.elements.article, 'data-getFTR-reference-url');
    }

    articleReferences = async (element): Promise<void> => {
        this.elements.articleReferences = element.closest(this.selectors.articleReferences);
        this.elements.article = this.domUtils.getElement<HTMLElement>(this.selectors.article);
        this.setGetFTRSource(this.elements.articleReferences, this.elements.article, 'data-getFTR-reference-url');
    }

    articleCitedBy = async (): Promise<void> =>{
        this.elements.articleCitedBy = this.domUtils.getElement<HTMLElement>(this.selectors.articleCitedBy);
        this.elements.articleCitedByAccordionControl = this.domUtils.getElement<HTMLElement>(this.selectors.articleCitedByAccordionControl, this.elements.articleCitedBy);
        this.setGetFTRSource(this.elements.articleCitedBy, this.elements.articleCitedByAccordionControl,  'data-getFTR-citedby-url');
    }

    private handleTabScroll(source: HTMLElement): void {
        const firstPlaceholder = source?.querySelector<HTMLElement>(this.selectors.getFTRPlaceholder);
        const firstPlaceholderOffsetTop = firstPlaceholder?.offsetTop;
        const tabContentScrollY = this.elements.tabContent?.scrollTop;
        const getFTRPlaceholdersLength = this.domUtils.getElements(this.selectors.getFTRPlaceholder, this.elements.source).length; 

        if ((firstPlaceholder && ((firstPlaceholderOffsetTop - 500) < tabContentScrollY) || (getFTRPlaceholdersLength && getFTRPlaceholdersLength < 4))) {
            this.getGetFTRElements()
        }
    }

    private handleWindowScroll = async (): Promise<void> => {
        if (!this.elements.articleReferences && !this.elements.articleCitedBy) {
            return;
        }

        const scrollPosition = window.scrollY;
        const articleCitedByOffsetTop = this.elements.articleCitedBy?.offsetTop
        const targetElement = scrollPosition >= articleCitedByOffsetTop
            ? this.elements.articleCitedBy
            : this.elements.articleReferences;

        const firstPlaceholder = targetElement?.querySelector<HTMLElement>(this.selectors.getFTRPlaceholder);
        const firstPlaceholderOffsetTop = firstPlaceholder?.offsetTop;

        if (firstPlaceholder && this.isSafeToFetch && (firstPlaceholderOffsetTop - 500) < scrollPosition) {
            if (targetElement === this.elements.articleReferences) {
                await this.articleReferences(this.elements.articleReferences);
            } else {
                await this.articleCitedBy();
            }
        }
    }

    private fetchData(elementsData: string[]): void {
        const requestBody = {
            pubDoi: this.texts.doi,
            associatedCitations: elementsData,
            requestSource: this.texts.requestSource
        }

        this.isSafeToFetch = false;

        fetch(this.texts.apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        })
            .then((response) => response.json())
            .then((results) => {
                results.forEach((item: object) => {
                    this.getMatchingPlaceholder(item['doi']);
                    this.isEntitlement(item);
                    this.getFTRContentEmpty();
                    this.removePlaceholder();
                })
                this.isSafeToFetch = true;
                this.elements.source === this.elements.tabReferences ? this.handleTabScroll(this.elements.source) : this.handleWindowScroll();
            })
            .catch((error) => {
                console.error('Error:', error);  // eslint-disable-line no-console
            })
    }

    private getMatchingPlaceholder = (doi: string) =>{
        this.elements.getFTRPlaceholderElements?.forEach((element) => {
            const htmlElement = element as HTMLElement;
            const elementDoi = htmlElement.previousElementSibling.textContent;
            if (elementDoi === doi) {
                this.elements.getFTRPlaceholderMatched.push(htmlElement);
            }
        })
    }

    private isEntitlement = (item: object): void => {
        if (item['entitlement']) {
            this.isButtonVisible = true;
            this.getFTRButton(item['entitlement'])
        } else if (!this.texts.isCrossrefEnabled && item['doi'] && item['fallback']) {
            this.isButtonVisible = true;
            this.getFTRButton(item['fallback']['url'])
        } else if(this.texts.isCrossrefEnabled && item['fallback']){
            this.getFTRLink(item['fallback'])
        }
    }

    private getFTRButton = (data: object | string): void => {
        let isIconVisible = false;
        let iconName = '';
        let iconEl = '';
        const isEntitlement = typeof data === 'object';
        const btnText = isEntitlement ? data['label'] : 'View';
        const btnLink = isEntitlement ? data['url'] : data;
        const tooltip = isEntitlement && data['tooltip'] ? data['tooltip'] : false;

        if (isEntitlement && (data['entitled'] === 'yes' || (data['entitled'] === 'maybe' && data['type'] === 'vor'))) {
            isIconVisible = true;
            iconName = 'icon-getFTR-na';
        } else if (isEntitlement && data['entitled'] === 'no' && data['type'] === 'av') {
            isIconVisible = true;
            iconName = 'icon-getFTR-av';
        }

        iconEl = isIconVisible ? `<i aria-hidden="true" class="${iconName}"></i>` : '';
        this.texts.getFTRButton =
            `<a href="${btnLink}"  aria-label="${tooltip ? tooltip : btnText}" target="_blank" data-tooltip="${tooltip}" class="getFTR__btn ${tooltip ? 'getFTR__tooltip' : ''}"  tabindex="0">
               ${iconEl}
               ${btnText}
            </a>`;


        this.elements.getFTRPlaceholderMatched?.forEach((element) => {
            if(!element.querySelector(this.selectors.getFTRBtn)){
                element.insertAdjacentHTML('afterbegin', this.texts.getFTRButton)
            }
        })
    }

    private getFTRLink = (data: object): void =>{
        this.texts.getFTRLink =
            `<a href="${data['url']}"  target="_blank" class="getFTR__link">
               ${data['label']}
            </a>`;

        this.elements.getFTRPlaceholderMatched?.forEach((element) => {
            element.insertAdjacentHTML('afterbegin', this.texts.getFTRLink)
        })
    }

    private getFTRContentEmpty(): void {
        this.texts.getFTRButton = '';
        this.texts.getFTRLink = '';
        this.isButtonVisible = false;
    }

    private removePlaceholder(): void {
        this.elements.getFTRPlaceholderMatched?.forEach((element) => {
            element.classList.remove('getFTR__placeholder');
        })
    }
}

export default GetFTR;

export {
    Elements as GetFTRElements,
    Selectors as GetFTRSelectors,
    ClassList as GetFTRClassList,
    Texts as GetFTRTexts
}